import React, { useState, useEffect } from 'react';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import classNames from 'classnames';
import ErrorIcon from '@material-ui/icons/Error';
import InfoIcon from '@material-ui/icons/Info';
import CloseIcon from '@material-ui/icons/Close';
import { amber, green } from '@material-ui/core/colors';
import IconButton from '@material-ui/core/IconButton';
import Snackbar from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import WarningIcon from '@material-ui/icons/Warning';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { setNotification } from '../../redux/admin/adminActions';
import { Notification } from '../../redux/admin/adminState';

interface NotificationBarProps {
    notification: Notification | null;
}

const variantIcon = {
    success: CheckCircleIcon,
    warning: WarningIcon,
    error: ErrorIcon,
    info: InfoIcon,
};

const useStyles = makeStyles((theme) => ({
    success: {
        backgroundColor: green[600],
    },
    error: {
        backgroundColor: theme.palette.error.dark,
    },
    info: {
        backgroundColor: theme.palette.primary.dark,
    },
    warning: {
        backgroundColor: amber[700],
    },
    icon: {
        fontSize: 20,
    },
    iconVariant: {
        opacity: 0.9,
        marginRight: theme.spacing(1),
    },
    message: {
        display: 'flex',
        alignItems: 'center',
    },
}));

const NotificationBar: React.FC<NotificationBarProps> = ({ notification }) => {
    const [open, setOpen] = useState(false);
    const classes = useStyles();
    const variant = notification ? notification.type : 'info';
    const message = notification ? notification.content : '';
    const stayOpen = notification ? notification.stayOpen : false;
    // Note: content can be string or string[] in practice, even though type says string
    const messageContent = message as string | string[];
    const Icon = variantIcon[variant];
    const defaultAutoHideDuration = 6000;

    const dispatch = useDispatch();

    const handleClose = (
        event: React.SyntheticEvent,
        reason?: string,
    ): void => {
        if (reason === 'clickaway') {
            return;
        }
        dispatch(setNotification(null));
        setOpen(false);
    };

    useEffect(() => {
        if (notification !== null) {
            setOpen(true);
        } else {
            setOpen(false);
        }
    }, [notification]);

    return (
        <Snackbar
            open={open}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            {...(!stayOpen && { autoHideDuration: defaultAutoHideDuration })}
            onClose={handleClose}
        >
            <SnackbarContent
                className={classNames(classes[variant])}
                aria-describedby="client-snackbar"
                message={
                    <span id="client-snackbar" className={classes.message}>
                        <Icon
                            className={classNames(
                                classes.icon,
                                classes.iconVariant,
                            )}
                        />
                        {Array.isArray(messageContent) ? (
                            <ul>
                                {messageContent.map((line, index) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                    <li key={`message_line_${index}`}>
                                        {line}
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            messageContent
                        )}
                    </span>
                }
                action={[
                    <IconButton
                        key="close"
                        aria-label="close"
                        color="inherit"
                        onClick={handleClose}
                    >
                        <CloseIcon className={classes.icon} />
                    </IconButton>,
                ]}
            />
        </Snackbar>
    );
};

export default NotificationBar;
